/**
 * What a cart line costs RIGHT NOW, according to the live product document.
 *
 * WHY THIS EXISTS. A cart line stores the price that was on the product the
 * moment it was added, and the cart is persisted (Firestore for signed-in
 * buyers, localStorage otherwise) with no expiry, so a line can sit there for
 * months. The server never reads that stored figure: createPaymentIntent prices
 * every line from the product document (see resolveItemPriceAud in
 * functions/src/index.ts). So a seller changing their price left the cart
 * showing a total the buyer would not be charged, in either direction, with
 * nothing on screen to say so.
 *
 * This mirrors resolveItemPriceAud branch for branch, deliberately including the
 * details the client's own `cartPriceAud` glosses over: the server compares
 * `discountedPrice < price` rather than treating any discount as an override,
 * and it prices a subscription off `subscriptionPrice` rather than `price`.
 * Anywhere the two could disagree, the server wins, because the server is what
 * charges the card. Keep the two in step.
 *
 * NULL MEANS "DO NOT TOUCH THIS LINE", not "free". Three cases return it: an
 * add-on and any other line with no product document behind it, a
 * pay-what-you-want line (the amount is the buyer's, not the seller's, and it
 * rides to checkout in `price`, so repricing it would overwrite what they typed),
 * and a tiered line whose tier has since been removed from the listing. The
 * caller keeps the stored price for those and lets checkout be the one to
 * refuse, which it already does with a specific reason.
 */

export type CartPricingProduct = {
  pricingModel?: string | null;
  price?: number | null;
  discountedPrice?: number | null;
  subscriptionPrice?: number | null;
  priceTiers?: Array<{ id?: unknown; price?: unknown }> | null;
};

const toCents = (n: number) => Math.round(n * 100) / 100;

export function currentLinePriceAud(
  product: CartPricingProduct | undefined | null,
  tierId?: string,
): number | null {
  if (!product) return null;

  // Buyer-named. Never overwritten from the doc.
  if (product.pricingModel === 'pwyw') return null;

  // Free short-circuits before any price field is read, exactly as the server
  // does, so a listing switched to Free after a price was typed cannot show the
  // leftover amount. See src/lib/free-pricing.ts for the full history.
  if (product.pricingModel === 'free') return 0;

  if (product.pricingModel === 'tiered') {
    const tiers = Array.isArray(product.priceTiers) ? product.priceTiers : [];
    // String-compared: tier ids are written as both numbers and strings across
    // the product forms and the import path.
    const tier = tierId
      ? tiers.find(t => String(t?.id) === String(tierId))
      : undefined;
    if (!tier) return null;
    const price = Number(tier.price);
    return Number.isFinite(price) ? toCents(price) : null;
  }

  if (product.pricingModel === 'subscription') {
    const price = Number(product.subscriptionPrice);
    return Number.isFinite(price) ? toCents(price) : null;
  }

  // Standard. The server picks the discount only when it is genuinely LOWER,
  // rather than treating any present `discountedPrice` as an override.
  const dp = product.discountedPrice;
  const listed = Number(product.price);
  const discounted = Number(dp);
  const chosen = (dp !== undefined && dp !== null && Number.isFinite(discounted) && discounted < listed)
    ? discounted
    : listed;
  return Number.isFinite(chosen) ? toCents(chosen) : null;
}

/**
 * Whether a stored line price and the live one differ by enough to tell the
 * buyer about.
 *
 * A cent of tolerance, because both sides round to cents independently and a
 * half-cent of float dust on a legacy document is not a price change. It is
 * noise, and a "the price changed" banner that fires on noise is one buyers
 * learn to ignore before the day it means something.
 */
export function linePriceChanged(storedAud: number, liveAud: number | null): boolean {
  if (liveAud === null) return false;
  const stored = Number(storedAud);
  if (!Number.isFinite(stored)) return false;
  // Compared as whole cents rather than as a `>= 0.01` gap: binary floats put
  // 10.01 - 10 at 0.00999999…, so the obvious spelling of "differs by at least a
  // cent" is the one thing that misses a one-cent change.
  return Math.round(stored * 100) !== Math.round(liveAud * 100);
}

/**
 * Whether to tell the buyer the seller has repriced this line.
 *
 * Three prices meet here and they do not all come from the same place, which is
 * the whole reason this is a function rather than one comparison:
 *
 *   - `storedAud`     what the line was added at. Captured from the DISCOUNT
 *                     OVERLAY, because that is what the card and the product
 *                     page quote.
 *   - `liveAud`       what the server would charge now. From the product
 *                     DOCUMENT, because resolveItemPriceAud reads the
 *                     `discountedPrice` mirror off it directly.
 *   - `advertisedAud` what the overlay says now.
 *
 * The overlay deliberately runs ahead of the once-a-minute sweepDiscounts
 * mirror so a sale badge appears the minute it is due. So for up to a minute
 * either side of a window opening or closing, the overlay and the document
 * disagree, legitimately, about the same unchanged listing. Comparing a stored
 * overlay price against a live document price across that gap accuses the seller
 * of a reprice that never happened: a buyer who added an item the instant a sale
 * opened was told immediately that it had gone "Up from" the price they had just
 * been shown.
 *
 * So a disagreement between the overlay and the document means "a window is
 * moving", and nothing is claimed until they agree again. The flag is delayed by
 * at most one sweep, and a real reprice on a line with no discount in play has
 * the two in step and reports immediately.
 *
 * A null `advertisedAud` is not a disagreement: it means the overlay has no
 * opinion on this line at all (no product document yet, pay-what-you-want, a
 * deleted tier), which is exactly when `liveAud` is null too.
 */
export function linePriceReprice(
  storedAud: number,
  liveAud: number | null,
  advertisedAud: number | null,
): boolean {
  const midSweep = advertisedAud !== null && linePriceChanged(advertisedAud, liveAud);
  return !midSweep && linePriceChanged(storedAud, liveAud);
}
